import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { useToast } from './ui/ToastProvider';
import { transactionStore, ManualSettlement } from '../utils/transactionStore';
const MakerManualSettlementTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingSettlements, setPendingSettlements] = useState<ManualSettlement[]>([]);
  const [rejectedSettlements, setRejectedSettlements] = useState<ManualSettlement[]>([]);
  const [selectedSettlement, setSelectedSettlement] = useState<ManualSettlement | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const refreshSettlements = () => {
    setPendingSettlements(transactionStore.getPendingManualSettlements());
    setRejectedSettlements(transactionStore.getRejectedManualSettlements());
  };
  useEffect(() => {
    refreshSettlements();
    const unsubscribe = transactionStore.subscribe(refreshSettlements);
    return unsubscribe;
  }, []);
  const handleAction = (settlement: ManualSettlement, action: 'resubmit' | 'discard') => {
    setSelectedSettlement(settlement);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selectedSettlement || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitManualSettlement(selectedSettlement.id, reason);
        showToast(`Manual settlement resubmitted for ${selectedSettlement.transactionId}`, 'success');
      } else {
        transactionStore.discardManualSettlement(selectedSettlement.id, reason);
        showToast(`Manual settlement discarded for ${selectedSettlement.transactionId}`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelectedSettlement(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewDetails = (settlement: ManualSettlement) => {
    setSelectedSettlement(settlement);
    setDetailsDrawerOpen(true);
  };
  const pendingColumns: Column<ManualSettlement>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1133">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1134">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1135">
          {row.mtoName}
        </Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1136">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1137">
          {row.beneficiaryName}
        </Typography>
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-1138">
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1139" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1140">
          {new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}
        </Typography>
  }];
  const rejectedColumns: Column<ManualSettlement>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1141">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1142">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1143">
          {row.mtoName}
        </Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1144">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'makerReason',
    label: 'Original Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1145">
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1146">
          {row.checkerComment}
        </Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1147">
          {new Date(row.checkerActedAt || '').toLocaleString()}
        </Typography>
  }];
  const rejectedRowActions: RowAction<ManualSettlement>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }, {
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Discard',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1148">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1149">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1150">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1152">
                <Typography variant="body2" fontWeight={500} data-id="element-1153">
                  Pending
                </Typography>
                <Chip label={pendingSettlements.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1154" />
              </Box>} data-id="element-1151" />
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1156">
                <Typography variant="body2" fontWeight={500} data-id="element-1157">
                  Rejected
                </Typography>
                <Chip label={rejectedSettlements.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1158" />
              </Box>} data-id="element-1155" />
        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending Manual Settlements" data={pendingSettlements} columns={pendingColumns} keyField="id" emptyStateMessage="No pending manual settlements" initialVisibleColumns={['transactionId', 'mtoName', 'amountFCY', 'beneficiaryName', 'makerReason', 'status', 'requestedAt']} onRowClick={handleViewDetails} data-id="element-1159" /> : <DataTable title="Rejected Manual Settlements" data={rejectedSettlements} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected manual settlements" initialVisibleColumns={['transactionId', 'mtoName', 'amountFCY', 'makerReason', 'checkerComment', 'checkerActedAt']} onRowClick={handleViewDetails} data-id="element-1160" />}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1161">
        <DialogTitle data-id="element-1162">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1163">
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1164" />
            <Typography variant="h6" fontWeight={600} data-id="element-1165">
              {actionType === 'resubmit' ? 'Resubmit' : 'Discard'} Manual
              Settlement
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1166">
          <Box sx={{
          mt: 1
        }} data-id="element-1167">
            {selectedSettlement && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1168">
                  Transaction ID:{' '}
                  <strong data-id="element-1169">{selectedSettlement.transactionId}</strong>
                </Typography>

                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1170">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1171">
                    Original Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1172">
                    {selectedSettlement.resubmitReason || selectedSettlement.makerReason}
                  </Typography>

                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1173">
                    Checker's Rejection Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1174">
                    {selectedSettlement.checkerComment}
                  </Typography>
                </Paper>
              </>}

            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Discarding'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'discarding'} this settlement...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1175" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1176">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1177">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1179" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1180" />} data-id="element-1178">
            {actionType === 'resubmit' ? 'Resubmit' : 'Discard'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      {selectedSettlement && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedSettlement(null);
    }} transaction={{
      id: selectedSettlement.transactionId,
      mtoName: selectedSettlement.mtoName,
      mtoRef: `MTO-${selectedSettlement.transactionId}`,
      bankRef: `BNK-${selectedSettlement.transactionId}`,
      branchCode: '',
      agentCode: '',
      payoutType: selectedSettlement.payoutType,
      amountFCY: selectedSettlement.amountFCY,
      currencyFCY: selectedSettlement.currencyFCY,
      amountPKR: (parseFloat(selectedSettlement.amountFCY) * 15).toString(),
      corridor: 'GH-UK',
      purposeCode: 'FAM',
      purposeText: 'Personal Remittance',
      currentState: selectedSettlement.currentState as any,
      lifecycleStatus: 'FAILED' as any,
      createdAt: selectedSettlement.requestedAt,
      updatedAt: selectedSettlement.requestedAt,
      remitterName: 'Remitter Name',
      beneficiaryName: selectedSettlement.beneficiaryName,
      beneficiaryIBAN: selectedSettlement.beneficiaryIBAN,
      railRefs: {},
      stateHistory: []
    } as any} data-id="element-1181" />}
    </Box>;
};
export default MakerManualSettlementTable;